import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { v4 } from "uuid";
import { useToast } from "@/shared/hooks/use-toast";
import { NavLink, useNavigate, useParams } from "react-router";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Loader2, Search } from "lucide-react";
import {
  useQpmRequirementCreateMutation,
  useQpmRequirementListQuery,
} from "@/services/backend/qpm/requirements/api/requirement-api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { useCustomersCustomerShowQuery } from "@/services/backend/customers/api/customer-api";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import {
  MilestonePage,
  MilestonePageActions,
  MilestonePageContent,
  MilestonePageHeader,
  MilestonePageTitle,
} from "@/shared/bsm/core/milestone/components/ui/milestone-page";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  useQpmBuildingAddressListQuery,
  useQpmBuildingServiceAddressGetQuery,
} from "@/services/backend/qpm/buildings/api/building-api";
import { TextInput } from "@/shared/components/form/text-input";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import { CatalogSourceSelect } from "@/routes/base-data/processes/qpm/catalog/_components/catalog-source-select";
import { useProcessCatalogSelection } from "@/services/backend/qpm/catalog/hooks/use-process-catalog-selection";

export function RequirementsIndexRoute() {
  const { processId } = useParams();

  const {
    data: process,
    error: processDataError,
    isLoading: processDataIsLoading,
  } = useProcessesProcessShowQuery({
    id: processId ?? "",
  });

  if (processDataIsLoading) {
    return <Loader2 className="animate-spin" />;
  }

  if (!processId || !process) {
    return <div>Fehler beim Laden des Vorgangs!</div>;
  }

  if (processDataError) {
    return <RTKQueryErrorAlert error={processDataError} />;
  }

  if (!process.customerId) {
    return <div>{t("Diesem Vorgang ist noch kein Kunde zugeordnet.")}</div>;
  }

  return (
    <MilestonePage>
      <MilestonePageHeader>
        <MilestonePageTitle>{t("Bestandsaufnahmmen")}</MilestonePageTitle>
        <MilestonePageActions>
          <CreateNewRequirement
            processId={processId!}
            customerId={process.customerId}
          />
        </MilestonePageActions>
      </MilestonePageHeader>
      <MilestonePageContent>
        <Card>
          <RequirementHistoryTable
            processId={processId}
            customerId={process.customerId}
          />
        </Card>
      </MilestonePageContent>
    </MilestonePage>
  );
}

type CreateNewRequirementProps = {
  processId: string;
  customerId: string;
};

/**
 * TODO-LT: Refactor - Build Create RequirementDialog where the user can choose to create a new requirement, or copy data from old requirements
 * @param processId
 * @param customerId
 * @constructor
 */
export function CreateNewRequirement({
  processId,
  customerId,
}: CreateNewRequirementProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const [subject, setSubject] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const closeDialogAndNavigate = useCallback(
    (requirementId: string) => {
      setOpen(false);
      setSubject("");
      setDescription("");
      navigate(`${requirementId}`);
    },
    [navigate],
  );

  const [createRequirement, { isLoading, data, error, reset }] =
    useQpmRequirementCreateMutation();

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    error: errorCustomer,
  } = useCustomersCustomerShowQuery({ id: customerId });
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const { data: addressList } = useQpmBuildingAddressListQuery(
    { customerId },
    { skip: !customerId },
  );

  const serviceObjectIds = useMemo(
    () => addressList?.addresses?.[0]?.serviceObjectIds ?? [],
    [addressList?.addresses],
  );

  const [selectedServiceIndex, setSelectedServiceIndex] = useState(0);
  const {
    catalogId,
    setCatalogId,
    isLoading: isLoadingCatalogs,
    error: catalogError,
  } = useProcessCatalogSelection(customerId);

  const selectedServiceObjectId = useMemo(() => {
    if (!serviceObjectIds.length) return undefined;
    return serviceObjectIds[selectedServiceIndex];
  }, [serviceObjectIds, selectedServiceIndex]);

  const { data: selectedServiceAddressResponse } =
    useQpmBuildingServiceAddressGetQuery(
      { serviceObjectId: selectedServiceObjectId ?? "" },
      { skip: !selectedServiceObjectId },
    );

  const selectedServiceAddressLabel = useMemo(() => {
    const a = selectedServiceAddressResponse?.address;
    if (!a) return "";
    return `${a.street}, ${a.postalCode} ${a.city}`;
  }, [selectedServiceAddressResponse?.address]);

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error) });
      reset();
    }
  }, [error, toast, reset]);

  useEffect(() => {
    if (data) {
      closeDialogAndNavigate(data.id);
    }
  }, [data, closeDialogAndNavigate]);

  const onClick = useCallback(() => {
    if (isLoading || !customer || !selectedServiceObjectId) return;
    const id = v4();

    const trimmedSubject = subject.trim();
    const trimmedDescription = description.trim();

    createRequirement({
      requirementId: id,
      customerId,
      processId,
      serviceObjectId: selectedServiceObjectId,
      companyId: process?.companyId ?? "",
      branchId: process?.branches[0] ?? "",
      catalogId: catalogId || undefined,
      subject: trimmedSubject !== "" ? trimmedSubject : undefined,
      description: trimmedDescription !== "" ? trimmedDescription : undefined,
    });
  }, [
    isLoading,
    customer,
    process,
    selectedServiceObjectId,
    catalogId,
    createRequirement,
    customerId,
    processId,
    subject,
    description,
  ]);

  if (errorCustomer) {
    return <RTKQueryErrorAlert error={errorCustomer} />;
  }

  if (isLoadingCustomer) {
    return <Loader2 className="animate-spin" />;
  }

  const selectorDisabled = serviceObjectIds.length <= 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSubject("");
          setDescription("");
        }
      }}
    >
      <DialogTrigger asChild>
        <Button disabled={isLoading || isLoadingCustomer}>
          {t("Erstellen")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("Neue Bestandsaufnahme erstellen")}</DialogTitle>
        </DialogHeader>

        <RTKQueryErrorAlert error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-muted-foreground text-sm">
              {t(
                "Bitte wählen Sie eine Serviceadresse für die die Bestandsaufnahme erstellt werden soll.",
              )}
            </div>

            {selectorDisabled ? (
              <div className="rounded-md border px-3 py-2 text-sm">
                {selectedServiceAddressLabel || t("Keine Serviceadresse")}
              </div>
            ) : (
              <Select
                value={selectedServiceObjectId}
                onValueChange={(value) => {
                  const idx = serviceObjectIds.findIndex(
                    (id: string) => id === value,
                  );
                  setSelectedServiceIndex(idx >= 0 ? idx : 0);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("Serviceadresse auswählen")} />
                </SelectTrigger>
                <SelectContent>
                  {serviceObjectIds.map((id: string) => (
                    <ServiceAddressSelectItem key={id} serviceObjectId={id} />
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-muted-foreground text-sm">
              {t("Katalog für die Bestandsaufnahme")}
            </div>
            <CatalogSourceSelect
              value={catalogId}
              onChange={setCatalogId}
              customerId={customerId}
            />
            {isLoadingCatalogs && (
              <div className="text-muted-foreground text-xs">
                {t("Kataloge werden geladen")}
              </div>
            )}
            <RTKQueryErrorAlert error={catalogError} />
          </div>

          <div className="space-y-2">
            <div className="text-muted-foreground text-sm">
              {t("Sie können Optional einen Titel oder Thema vergeben")}
            </div>
            <TextInput
              label={t("Betreff")}
              field="subject"
              text={subject}
              onTextChange={setSubject}
              errors={null}
            />
          </div>

          <div className="space-y-2">
            <div className="text-muted-foreground text-sm">
              {t("Die Beschreibung ist auch optional.")}
            </div>
            <TextareaInput
              label={t("Beschreibung")}
              field="description"
              text={description}
              onTextChange={setDescription}
              errors={null}
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={onClick}
              disabled={isLoading || !selectedServiceObjectId}
            >
              {t("Erstellen")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ServiceAddressSelectItem({
  serviceObjectId,
}: {
  serviceObjectId: string;
}) {
  const { data } = useQpmBuildingServiceAddressGetQuery(
    { serviceObjectId },
    { skip: !serviceObjectId },
  );

  const label = data?.address
    ? `${data.address.street}, ${data.address.postalCode} ${data.address.city}`
    : serviceObjectId;

  return <SelectItem value={serviceObjectId}>{label}</SelectItem>;
}

function RequirementHistoryTable({
  processId,
  customerId,
}: {
  processId: string;
  customerId: string;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useQpmRequirementListQuery({
    process: {
      active: true,
      values: [processId],
    },
    customer: {
      active: true,
      values: [customerId],
    },
  });

  if (isLoading) {
    return <RequirementHistoryTableSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const sortedRequirements = [...(list?.items ?? [])].sort(byCreatedAtAndId);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Uhrzeit")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRequirements.map((requirement) => (
          <TableRow key={requirement.id}>
            <TableCell>{formatDate(requirement.createdAt)}</TableCell>
            <TableCell>{formatTime(requirement.createdAt)}</TableCell>
            <TableCell>
              <NavLink to={`${requirement.id}`}>
                <DocumentNumberById documentId={requirement.id} />
              </NavLink>
            </TableCell>
            <TableCell>{requirement.subject}</TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <NavLink to={`${requirement.id}`}>
                  <Button variant="ghost">
                    <Search className="h-4 w-4" />
                  </Button>
                </NavLink>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {list && list.items && list.items.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-center">
              {t("Keine Bestandsaufnahmen gefunden")}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function byCreatedAtAndId(
  a: { createdAt: string; id: string },
  b: { createdAt: string; id: string },
) {
  const createdAtOrder = b.createdAt.localeCompare(a.createdAt);
  if (createdAtOrder !== 0) return createdAtOrder;
  return b.id.localeCompare(a.id);
}

function RequirementHistoryTableSkeleton() {
  return (
    <Table className="animate-pulse">
      <TableHeader>
        <TableRow>
          <TableHead>{t("Datum")}</TableHead>
          <TableHead>{t("Nummer")}</TableHead>
          <TableHead className="w-full">{t("Vorgang Thema")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
    </Table>
  );
}
